import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { certificateService } from '../services';

function UploadCertificatePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [certificateInfo, setCertificateInfo] = useState({
    issuer: '',
    certificateNumber: '',
    expiryDate: '',
    organizationName: user?.organization || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setError('');
    
    if (selectedFile) {
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(selectedFile.type)) {
        setError('Invalid file type. Please upload a PDF, PNG, or JPG file.');
        return;
      }
      
      // Check file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (selectedFile.size > maxSize) {
        setError('File is too large. Maximum size is 10MB.');
        return;
      }
      
      setFile(selectedFile);
      
      // Create a preview for images
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        // For PDFs, just clear the image preview
        setPreview(''); 
      }
    }
  };
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCertificateInfo(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');
    
    try {
      if (!file) {
        throw new Error('Please select a certificate file to upload');
      }
      
      if (!certificateInfo.issuer || !certificateInfo.expiryDate) {
        throw new Error('Please complete all required fields');
      }
      
      // Validate expiry date is in the future
      const expiryDate = new Date(certificateInfo.expiryDate);
      if (expiryDate < new Date()) {
        throw new Error('Certificate expiry date must be in the future');
      }
      
      // Create form data for file upload
      const formData = new FormData();
      formData.append('file', file); // 'file' is the expected field name by the backend
      formData.append('name', `${certificateInfo.issuer} Certificate` || 'Food Safety Certificate');
      formData.append('issuer', certificateInfo.issuer);
      formData.append('certificate_number', certificateInfo.certificateNumber || '');
      formData.append('expiry_date', certificateInfo.expiryDate);
      formData.append('organization_name', certificateInfo.organizationName || user?.organization || '');
      
      // Use certificateService to upload the certificate
      const response = await certificateService.uploadCertificate(formData);
      
      console.log('Certificate uploaded successfully:', response);
      
      setSuccess('Certificate uploaded successfully! Our team will review it shortly.');
      
      // Redirect to profile page after 2 seconds
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
      
    } catch (err) {
      console.error('Error uploading certificate:', err);
      // Extract error message from API response if available
      const errorMessage = 
        err.message ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to upload certificate. Please try again.';
      setError(errorMessage);
      
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Food Safety Certificate Upload</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Please upload your food safety certificate for verification
          </p>
        </div>
        
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          {/* Success message */}
          {success && (
            <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">{success}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Error message */}
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Information box */}
          <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  To become a verified food donor, you need to provide your food safety certificate. 
                  This helps ensure all donated food meets safety standards.
                </p>
              </div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
              <div className="sm:col-span-2">
                <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700">
                  Organization Name
                </label>
                <input
                  type="text"
                  name="organizationName"
                  id="organizationName"
                  value={certificateInfo.organizationName}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="issuer" className="block text-sm font-medium text-gray-700">
                  Certificate Issuing Authority *
                </label>
                <input
                  type="text"
                  name="issuer"
                  id="issuer"
                  value={certificateInfo.issuer}
                  onChange={handleChange}
                  placeholder="e.g., Kenya Bureau of Standards"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="certificateNumber" className="block text-sm font-medium text-gray-700">
                  Certificate Number
                </label>
                <input
                  type="text"
                  name="certificateNumber"
                  id="certificateNumber"
                  value={certificateInfo.certificateNumber}
                  onChange={handleChange}
                  placeholder="e.g., FS-2023-12345"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700">
                  Certificate Expiry Date *
                </label>
                <input
                  type="date"
                  name="expiryDate"
                  id="expiryDate"
                  value={certificateInfo.expiryDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]} // Today's date as minimum
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  required
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Certificate File *
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-primary-300 transition-colors cursor-pointer" onClick={() => document.getElementById('file-upload').click()}>
                  <div className="space-y-1 text-center">
                    {preview ? (
                      <img src={preview} alt="Certificate Preview" className="mx-auto h-40 object-contain" />
                    ) : file && file.type === 'application/pdf' ? (
                      <div className="flex flex-col items-center">
                        <svg 
                          className="h-12 w-12 text-red-500" 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                        <p className="mt-1 text-sm text-gray-600">PDF Document Selected</p>
                      </div>
                    ) : (
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                      >
                        <span>Upload a file</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                          required
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PDF, PNG, JPG up to 10MB
                    </p>
                    {file && (
                      <p className="text-xs text-gray-800 font-medium">
                        Selected file: {file.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="pt-5">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Uploading...' : 'Upload Certificate'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">What happens next?</h3>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <ol className="list-decimal space-y-3 ml-4">
            <li className="text-gray-700">
              <span className="font-medium">Review:</span> Our admin team will review your certificate within 1-2 business days.
            </li>
            <li className="text-gray-700">
              <span className="font-medium">Verification:</span> We may contact you if additional information is needed.
            </li>
            <li className="text-gray-700">
              <span className="font-medium">Approval:</span> Once approved, you'll be able to create food donation listings.
            </li>
            <li className="text-gray-700">
              <span className="font-medium">Reminders:</span> You'll receive notifications when your certificate is nearing expiry.
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default UploadCertificatePage;
